import { action, computed, observable } from 'mobx';
import React from 'react';
import {
  ApiMasterDataQuery,
  ApiMasterDataQueryMeasure,
} from '../../../common/api/api-interfaces';
import { CachedMasterDataService } from '../../../common/api/cached-master-data-service';
import { getApplicableVersionForDate } from '../../../common/filter/utils';
import { GetResponseForMasterQueryService } from '../../../common/services/GetResponseForQueryService';
import { payrollColors } from '../../../common/theme/color-constants';
import { TimeSliderStore } from '../../../common/timeslider/timeslider-store';
import {
  HasDependencies,
  hashString,
  VersionId,
} from '../../../common/v2/api/types';
import { payrollFields } from '../../../common/v2/constants';
import {
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
  Operations,
  PayrollDataFields,
} from '../../../common/v2/types';
import { getMomentDate } from '../../../common/v2/utils-date';
import { ApiMasterDataAdvancedQueryDimension } from './../../../common/api/api-interfaces';

export interface LabeldApiMasterDataMeasure extends ApiMasterDataQueryMeasure {
  label: string;
}

type LabeldApiMasterDataMeasureWithoutOperation = Omit<
  LabeldApiMasterDataMeasure,
  'operation'
>;

export const getPayrollMeasuresKey = (m: string) =>
  `pages:dashboards.payroll.common.${m}`;

export type PayrollMeasures =
  | PayrollDataFields.STANDARDIZED_ACTUAL_BASE_SALARY
  | PayrollDataFields.STANDARDIZED_EMPLOYEE_STATUTORY_PAYMENT_TOTAL
  | PayrollDataFields.STANDARDIZED_EMPLOYER_STATUTORY_PAYMENT_TOTAL
  | PayrollDataFields.STANDARDIZED_EMPLOYEE_NON_STATUTORY_PAYMENT_TOTAL
  | PayrollDataFields.STANDARDIZED_EMPLOYER_NON_STATUTORY_PAYMENT_TOTAL
  | PayrollDataFields.STANDARDIZED_TOTAL_BONUS
  | PayrollDataFields.STANDARDIZED_TOTAL_ALLOWANCE
  | PayrollDataFields.STANDARDIZED_TOTAL_PAYROLL_COST
  | PayrollDataFields.STANDARDIZED_TOTAL_BOARD_COMPENSATION
  | PayrollDataFields.STANDARDIZED_TOTAL_EMPLOYEE_AWARD
  | PayrollDataFields.STANDARDIZED_TOTAL_REIMBURSEMENT
  | PayrollDataFields.STANDARDIZED_TRAVEL_AND_COMMUTING
  | PayrollDataFields.STANDARDIZED_OVERTIME;

export interface PayrollPills {
  enabled: boolean;
  label: string;
  key: string;
}

export type AllowedPayrollMeasureOperations = Operations.SUM | Operations.AVG;

export class PayrollStore implements HasDependencies {
  constructor(
    readonly timeSliderStore: TimeSliderStore,
    readonly cachedMasterDataApiService: CachedMasterDataService
  ) {}

  public getDeps = () => [
    hashString([
      ...this.timeSliderStore.getDeps(),
      ...this.cachedMasterDataApiService.getDeps(),
    ]),
  ];

  public payroll1Ref: React.RefObject<any> = React.createRef<any>();
  public payroll2Ref: React.RefObject<any> = React.createRef<any>();
  public payroll3Ref: React.RefObject<any> = React.createRef<any>();

  public payroll1Id = 'payroll-1';
  public payroll2Id = 'payroll-2';
  public payroll3Id = 'payroll-3';

  @observable
  public selectedMeasureOperation: AllowedPayrollMeasureOperations =
    Operations.SUM;

  @action
  public setSelectedMeasureOperation = (
    newOperation: AllowedPayrollMeasureOperations
  ) => {
    this.selectedMeasureOperation = newOperation;
  };

  public ALL_MEASURE_FOR_PAYROLL_QUERY: LabeldApiMasterDataMeasureWithoutOperation[] =
    [
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_ACTUAL_BASE_SALARY,
        label: 'totalBaseSalary',
      },
      {
        dataType: DataTypes.PAYROLL,
        property:
          PayrollDataFields.STANDARDIZED_EMPLOYEE_STATUTORY_PAYMENT_TOTAL,
        label: 'totalEmployeeStatutoryPayments',
      },
      {
        dataType: DataTypes.PAYROLL,
        property:
          PayrollDataFields.STANDARDIZED_EMPLOYER_STATUTORY_PAYMENT_TOTAL,
        label: 'totalEmployerStatutoryPayments',
      },
      {
        dataType: DataTypes.PAYROLL,
        property:
          PayrollDataFields.STANDARDIZED_EMPLOYEE_NON_STATUTORY_PAYMENT_TOTAL,
        label: 'totalEmployeeNonStatutoryPayments',
      },
      {
        dataType: DataTypes.PAYROLL,
        property:
          PayrollDataFields.STANDARDIZED_EMPLOYER_NON_STATUTORY_PAYMENT_TOTAL,
        label: 'totalEmployerNonStatutoryPayments',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_TOTAL_BONUS,
        label: 'totalBonus',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_TOTAL_ALLOWANCE,
        label: 'totalAllowance',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_TOTAL_PAYROLL_COST,
        label: 'totalPayrollCost',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_TOTAL_BOARD_COMPENSATION,
        label: 'totalBoardCompensation',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_TOTAL_EMPLOYEE_AWARD,
        label: 'totalEmployeeAward',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_TOTAL_REIMBURSEMENT,
        label: 'totalReimbursement',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_TRAVEL_AND_COMMUTING,
        label: 'travelAndCommuting',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_OVERTIME,
        label: 'overtimePay',
      },
    ];

  @observable
  public MEASURE_FOR_PAYROLL_QUERY_WITHOUT_OPERATION: LabeldApiMasterDataMeasureWithoutOperation[] =
    [];

  @computed
  public get MEASURE_FOR_PAYROLL_QUERY(): LabeldApiMasterDataMeasure[] {
    return this.MEASURE_FOR_PAYROLL_QUERY_WITHOUT_OPERATION.map((m) => {
      return {
        ...m,
        operation: this.selectedMeasureOperation,
      };
    });
  }

  public ALL_MEASURE_FOR_EMPLOYEE_QUERY: LabeldApiMasterDataMeasureWithoutOperation[] =
    [
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_ACTUAL_BASE_SALARY,
        label: 'totalBaseSalary',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_TOTAL_BONUS,
        label: 'totalBonus',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_TOTAL_ALLOWANCE,
        label: 'totalAllowance',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_TOTAL_BOARD_COMPENSATION,
        label: 'totalBoardCompensation',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_TOTAL_EMPLOYEE_AWARD,
        label: 'totalEmployeeAward',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_TOTAL_REIMBURSEMENT,
        label: 'totalReimbursement',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_TRAVEL_AND_COMMUTING,
        label: 'travelAndCommuting',
      },
      {
        dataType: DataTypes.PAYROLL,
        property: PayrollDataFields.STANDARDIZED_OVERTIME,
        label: 'overtimePay',
      },
    ];

  @observable
  public MEASURE_FOR_EMPLOYEE_QUERY_WITHOUT_OPERATION: LabeldApiMasterDataMeasureWithoutOperation[] =
    [];

  @computed
  public get MEASURE_FOR_EMPLOYEE_QUERY(): LabeldApiMasterDataMeasure[] {
    return this.MEASURE_FOR_EMPLOYEE_QUERY_WITHOUT_OPERATION.map((m) => {
      return {
        ...m,
        operation: this.selectedMeasureOperation,
      };
    });
  }

  @observable
  public costToCompanyPills: PayrollPills[] = [];

  @observable
  public costToEmployeePills: PayrollPills[] = [];

  public payrollDataViewDimensions = () => {
    const payrollDimensions: DataFieldWithDataType[] = payrollFields;
    const baseDimensions: ApiMasterDataAdvancedQueryDimension[] = [
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.ORGANIZATION_LEVEL_1,
      },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.ORGANIZATION_LEVEL_2,
      },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.ORGANIZATION_LEVEL_3,
      },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.LOCATION_LEVEL_1,
      },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.LOCATION_LEVEL_2,
      },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.LOCATION_LEVEL_3,
      },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.TENURE_GROUP,
      },
      { dataType: DataTypes.EMPLOYEE, property: EmployeeDataFields.GENDER },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.JOB_GRADE_LEVEL_1,
      },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.JOB_GRADE_LEVEL_2,
      },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.JOB_GRADE_LEVEL_3,
      },
      { dataType: DataTypes.EMPLOYEE, property: EmployeeDataFields.AGE_GROUP },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.EMPLOYMENT_TYPE_LEVEL_1,
      },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.EMPLOYMENT_TYPE_LEVEL_2,
      },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.EMPLOYMENT_TYPE_LEVEL_3,
      },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.NATIONALITY_HIERARCHICAL_LEVEL_1,
      },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.NATIONALITY_HIERARCHICAL_LEVEL_2,
      },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.NATIONALITY_HIERARCHICAL_LEVEL_3,
      },
      { dataType: DataTypes.EMPLOYEE, property: EmployeeDataFields.JOB_TITLE },
      {
        dataType: DataTypes.EMPLOYEE,
        property: EmployeeDataFields.MARITAL_STATUS,
      },
    ];
    return [...payrollDimensions, ...baseDimensions];
  };

  @action
  public toggleCostToCompanyPills = (key: string) => {
    const index = this.costToCompanyPills.findIndex((k) => k.key === key);
    this.costToCompanyPills[index].enabled =
      !this.costToCompanyPills[index].enabled;
  };

  @action
  public toggleCostToEmployeePills = (key: string) => {
    const index = this.costToEmployeePills.findIndex((k) => k.key === key);
    this.costToEmployeePills[index].enabled =
      !this.costToEmployeePills[index].enabled;
  };

  @action
  public loadNonZeroMeasures = async (
    latestVersionId: VersionId,
    timeSliderRangeStartDate: string
  ) => {
    const query: ApiMasterDataQuery = {
      dataType: DataTypes.PAYROLL,
      measures: this.ALL_MEASURE_FOR_PAYROLL_QUERY.map(
        ({ property, dataType }) => ({
          property,
          dataType,
          operation: Operations.MAX,
        })
      ),
      filterItems: [
        {
          operation: Operations.GREATER_THAN,
          property: PayrollDataFields.VERSION_ID,
          values: [
            getApplicableVersionForDate(
              getMomentDate(timeSliderRangeStartDate),
              this.timeSliderStore.timeSliderConfig.firstMonthOfYear,
              latestVersionId
            ),
          ],
          dataType: DataTypes.PAYROLL,
        },
      ],
    };
    const result = await GetResponseForMasterQueryService(
      query,
      this.cachedMasterDataApiService,
      latestVersionId
    );
    this.MEASURE_FOR_PAYROLL_QUERY_WITHOUT_OPERATION =
      result?.dataPoints?.[0]?.measures
        ?.filter(({ value }) => Boolean(value))
        ?.map(({ property }) => {
          const measure = this.ALL_MEASURE_FOR_PAYROLL_QUERY.find(
            (m) => m.property === property
          ) as LabeldApiMasterDataMeasureWithoutOperation;
          return measure;
        }) ?? [];
    this.costToCompanyPills =
      this.MEASURE_FOR_PAYROLL_QUERY_WITHOUT_OPERATION.map((measure) => ({
        key: measure.property,
        label: measure.label,
        enabled: true,
      }));

    this.MEASURE_FOR_EMPLOYEE_QUERY_WITHOUT_OPERATION =
      this.MEASURE_FOR_PAYROLL_QUERY_WITHOUT_OPERATION.filter((m) =>
        this.ALL_MEASURE_FOR_EMPLOYEE_QUERY.find(
          (empM) => empM.property === m.property
        )
      );
    this.costToEmployeePills =
      this.MEASURE_FOR_EMPLOYEE_QUERY_WITHOUT_OPERATION.map((measure) => ({
        key: measure.property,
        label: measure.label,
        enabled: true,
      }));
  };

  public toDataSet = (
    dataMap: Record<string, any>,
    labels: string[],
    measures: string[],
    pills: Array<{
      enabled: boolean;
      label: string;
      key: string;
    }>,
    labelKey: (label: string) => string,
    options?: { backgroundColor?: (m: PayrollMeasures) => any; stack?: string }
  ) => {
    const datasets: any[] = [];
    const headings: string[] = [];
    measures?.forEach((measure: string) => {
      const d = labels.map((l1) => dataMap[l1]?.[measure] ?? 0);
      const legend = pills.find((val) => val.key === measure);
      if (legend && legend !== null) {
        const label = labelKey(legend.label);
        headings.push(label);
        if (legend.enabled) {
          datasets.push({
            label,
            fill: false,
            backgroundColor:
              options?.backgroundColor?.(measure as PayrollMeasures) ??
              payrollColors[measure as PayrollMeasures],
            borderColor: payrollColors[measure as PayrollMeasures],
            data: d,
            stack: options?.stack,
          });
        }
      }
    });
    return { datasets, headings, labels };
  };

  public combineMultipleDatamaps = (
    dataMaps: Array<Record<string, any>>,
    measures: string[]
  ) => {
    const combinedDataMap = dataMaps.reduce(
      (combined: Record<string, any>, current: Record<string, any>) => {
        const labels = Object.keys(current);
        labels.forEach((label) => {
          if (!combined[label]) {
            combined[label] = {};
          }
          measures.forEach((measure) => {
            if (!combined[label][measure]) {
              combined[label][measure] = 0;
            }
            combined[label][measure] += Number(current[label][measure]);
          });
        });
        return combined;
      },
      {}
    );
    return combinedDataMap;
  };
}
